/**
 * Creates a mutation observer.
 *
 * @param {Object} options - The options object.
 * @param {HTMLElement} options.target - The target element.
 * @param {Function} options.handleMutations - The handle mutations function.
 * @returns {MutationObserver} The mutation observer.
 */
export function createMutationObserver({ target, handleMutations }) {
    const observer = new MutationObserver((mutations) => {
        handleMutations(mutations);
    });

    observer.observe(target, { childList: true, subtree: true });

    return observer;
}

// ---------------------------------------------------------------------------
// Transaction Item IDs
// ---------------------------------------------------------------------------

/**
 * Numeric item-id scheme for transaction rows:
 *   itemId = section * ITEM_PART_BASE + table * ITEM_TABLE_BASE + row
 *
 * Each table holds up to ITEM_MAX_ROWS rows without running into the next
 * table, and the largest id (D.9 row 9,999,999) still fits a signed INT.
 *
 * Example: B.2 row 1 → 120000001, B.3 row 1 → 130000001.
 */
export const ITEM_PART_BASE = 100_000_000;
export const ITEM_TABLE_BASE = 10_000_000;
export const ITEM_MAX_ROWS = ITEM_TABLE_BASE - 1;

/** Legacy scheme (section * 1000 + table * 100 + row), kept for decoding only. */
const LEGACY_ITEM_ID_LIMIT = 10_000;

/**
 * Builds the item-id of a transaction row.
 *
 * @param {number} section - 1 (Part B), 2 (Part C), 3 (Part D)
 * @param {number} table - Table number within the section (1-based)
 * @param {number} row - Row number within the table (1-based)
 * @returns {number}
 */
export function itemIdOf(section, table, row) {
    return section * ITEM_PART_BASE + table * ITEM_TABLE_BASE + row;
}

/**
 * Splits an item-id into section, table and row. Also accepts legacy ids
 * (below 10,000) stored before the scheme was widened.
 *
 * @param {number|string} itemId
 * @returns {{section: number, table: number, row: number}}
 */
export function decodeItemId(itemId) {
    const id = parseInt(itemId, 10);
    if (id < LEGACY_ITEM_ID_LIMIT) {
        return {
            section: Math.floor(id / 1000),
            table: Math.floor((id % 1000) / 100),
            row: id % 100,
        };
    }
    return {
        section: Math.floor(id / ITEM_PART_BASE),
        table: Math.floor((id % ITEM_PART_BASE) / ITEM_TABLE_BASE),
        row: id % ITEM_TABLE_BASE,
    };
}

/**
 * Re-encodes a stored item-id (legacy or current) in the current scheme, so
 * it matches the item-id attributes rendered on the form.
 *
 * @param {number|string} itemId
 * @returns {number}
 */
export function toCurrentItemId(itemId) {
    const { section, table, row } = decodeItemId(itemId);
    return itemIdOf(section, table, row);
}

/**
 * Transforms transaction keys by adding a section.table prefix and
 * removing any parenthetical text from labels.
 *
 * Prefix is derived from the numeric item ID (see `decodeItemId`):
 *   section → 1 (Part B), 2 (Part C), 3 (Part D)
 *   table   → 1, 2, 3, 4
 *   prefix  = "section.table: "
 *
 * Example: itemId 120000001, label "Total Purchase/Acquisition Price (excluding any taxes...)"
 *   → "B.2: Total Purchase/Acquisition Price"
 *
 * @param {Object} transactions - Flat transactions { itemId: { label: value } }
 * @returns {Object} Transformed transactions with prefixed, cleaned keys
 */
export function transformTransactionKeys(transactions) {
    const result = {};
    const sectionPrefix = ["B", "C", "D"];

    Object.entries(transactions).forEach(([itemId, row]) => {
        const { section, table } = decodeItemId(itemId);
        const prefix = `${sectionPrefix[section - 1]}.${table}`;

        const transformedRow = {};
        Object.entries(row).forEach(([label, value]) => {
            const cleaned = label.replace(/\s*\([^)]*\)/g, "").trim();
            transformedRow[`${prefix}: ${cleaned}`] = value;
        });

        result[itemId] = transformedRow;
    });

    return result;
}

// ---------------------------------------------------------------------------
// Paper Claim Helpers
// ---------------------------------------------------------------------------

/**
 * Contact fields that are optional on the paper Claim Form.
 *
 * Paper claims are keyed into the online portal by a Claims Processor using
 * the `?paper_claim=true` administrative URL. Phone number and email address
 * are not required on the paper Claim Form, so when that URL is used the
 * portal must accept a submission with either or both left blank. Regular
 * online submissions keep these fields required.
 *
 * @type {string[]}
 */
export const PAPER_CLAIM_OPTIONAL_CONTACT_FIELDS = [
    "email_address",
    "confirm_email_address",
    "phone_number_1",
    "phone_number_2",
];

/**
 * Whether the page was opened with the `?paper_claim=true` administrative URL.
 *
 * @returns {boolean}
 */
export function isPaperClaim() {
    return window.location.search.includes("paper_claim=true");
}

/**
 * Makes the phone number and email address contact fields optional.
 *
 * The fields stay on the form so the value can be captured when the claimant
 * provided it; only the "required" rule is dropped. Format validation (valid
 * email address / valid phone number) still applies to any value entered.
 *
 * Safe to call more than once and when some of the fields are not rendered.
 *
 * @returns {void}
 */
export function disablePaperClaimContactValidation() {
    PAPER_CLAIM_OPTIONAL_CONTACT_FIELDS.forEach((name) => {
        const field = document.getElementById(name);
        if (field) {
            makeFieldOptional(field);
        }
    });
}

/**
 * Drops every "required" rule attached to a form field:
 * - the `required` attribute, used by native constraint validation and by the
 *   `<intl-phone-input>` element's own validate()
 * - the `required` token in `data-validate`, used by validateField()
 * - the asterisk on the field label
 *
 * @param {HTMLElement} field - The input or custom element to relax.
 * @returns {void}
 */
function makeFieldOptional(field) {
    field.removeAttribute("required");

    if (field.hasAttribute("data-validate")) {
        const rules = field
            .getAttribute("data-validate")
            .split(" ")
            .filter((rule) => rule && rule !== "required");
        field.setAttribute("data-validate", rules.join(" "));
    }

    const label = document.querySelector(`label[for="${field.id}"]`);
    if (label) {
        label.childNodes.forEach((node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                node.textContent = node.textContent.replace(/\*/g, "");
            }
        });
    }
}
